import { useNavigate } from 'react-router-dom';

export interface EnvironmentRow {
  id: string;
  name: string;
  endpoint_url?: string;
  environment_type: string;
  current_status?: string;
  uptime_percentage: number;
  avg_response_time_ms?: number;
  last_checked_at?: string;
  check_interval_minutes?: number;
}

interface EnvironmentsTableProps {
  environments: EnvironmentRow[];
  onEdit: (env: EnvironmentRow) => void;
  onDelete: (id: string) => void;
}

export default function EnvironmentsTable({ environments, onEdit, onDelete }: EnvironmentsTableProps) {
  const navigate = useNavigate();

  const getStatusBorderColor = (status: string | undefined) => {
    const colors: Record<string, string> = {
      healthy: 'border-l-green-500',
      degraded: 'border-l-yellow-500',
      down: 'border-l-red-500',
      unknown: 'border-l-gray-400',
    };
    const key = status?.toLowerCase() || 'unknown';
    return colors[key] || colors.unknown;
  };

  const formatCheckInterval = (minutes: number | undefined) => {
    if (!minutes) return null;
    if (minutes < 60) return `Every ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    return `Every ${hours}h`;
  };

  const getStatusBadge = (status: string | undefined) => {
    const normalized = status?.toLowerCase() || 'unknown';
    const badges: Record<string, string> = {
      healthy: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      degraded: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      down: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      unknown: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${badges[normalized] || badges.unknown}`}>
        {normalized.charAt(0).toUpperCase() + normalized.slice(1)}
      </span>
    );
  };

  const getEnvironmentTypeBadge = (type: string) => {
    const badges: Record<string, string> = {
      production: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
      staging: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      development: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      qa: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      uat: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${badges[type] || badges.production}`}>
        {type.toUpperCase()}
      </span>
    );
  };

  const formatLastChecked = (dateString: string | undefined) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return `${Math.floor(diffMinutes / 1440)}d ago`;
  };

  if (environments.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center transition-colors">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No environments</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Get started by creating a new monitoring environment.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden transition-colors">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Environment
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Endpoint URL
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Uptime
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Avg Response
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Last Checked
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {environments.map((env) => (
              <tr
                key={env.id}
                onClick={() => navigate(`/cloudops/endpoints/${env.id}`)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/cloudops/endpoints/${env.id}`); }} role="row" tabIndex={0}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
              >
                <td className={`px-6 py-4 whitespace-nowrap border-l-4 ${getStatusBorderColor(env.current_status)}`}>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {env.name}
                  </div>
                  {formatCheckInterval(env.check_interval_minutes) && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {formatCheckInterval(env.check_interval_minutes)}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={env.endpoint_url}>
                    {env.endpoint_url || '—'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getEnvironmentTypeBadge(env.environment_type)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5">
                    {(env.current_status?.toLowerCase() === 'degraded' || env.current_status?.toLowerCase() === 'down') && (
                      <span className="relative flex h-2.5 w-2.5">
                        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                          env.current_status?.toLowerCase() === 'down' ? 'bg-red-400' : 'bg-yellow-400'
                        }`}></span>
                        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                          env.current_status?.toLowerCase() === 'down' ? 'bg-red-500' : 'bg-yellow-500'
                        }`}></span>
                      </span>
                    )}
                    {getStatusBadge(env.current_status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {env.uptime_percentage.toFixed(2)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {env.avg_response_time_ms ? `${env.avg_response_time_ms.toFixed(0)}ms` : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatLastChecked(env.last_checked_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); onEdit(env); }}
                      className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                      title="Edit"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(env.id); }}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
